import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useGame } from '../context/GameContext';
import transactionService from '../services/transactionService';
import '../styles/stock-details-modal.css';

const StockDetailsModal = ({ stock, isOpen, onClose }) => {
  const { user } = useAuth();
  const { loadPortfolioData } = useGame();
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('1M');
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [buyQuantity, setBuyQuantity] = useState(1);
  const [sellQuantity, setSellQuantity] = useState(1);
  
  // Portfolio and validation states
  const [portfolio, setPortfolio] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [buyLoading, setBuyLoading] = useState(false);
  const [sellLoading, setSellLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const periods = [
    { label: '1M', value: '1M', days: 30 },
    { label: '3M', value: '3M', days: 90 },
    { label: '6M', value: '6M', days: 180 },
    { label: '1Y', value: '1Y', days: 365 }
  ];

  useEffect(() => {
    if (isOpen && stock) {
      fetchCompanyDetails();
      fetchChartData();
      fetchPortfolioData();
      setErrorMessage('');
      setSuccessMessage('');
    }
  }, [isOpen, stock, selectedPeriod]);

  const fetchPortfolioData = async () => {
    if (!user?._id && !localStorage.getItem('userId')) return;
    
    setPortfolioLoading(true);
    try {
      const userId = user._id || localStorage.getItem('userId');
      const response = await transactionService.getPortfolio(userId);
      
      if (response.success) {
        setPortfolio(response.data);
      }
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    } finally {
      setPortfolioLoading(false);
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${stock.ticker}`);
      const data = await response.json();
      if (data.status === 'OK' && data.results) {
        setCompanyDetails(data.results);
      }
    } catch (err) {
      console.error('Error fetching company details:', err);
    }
  };

  const fetchChartData = async () => {
    setLoading(true);
    try {
      const period = periods.find(p => p.value === selectedPeriod);
      
      // Use a more reliable date range - going back from recent trading days
      const toDate = new Date();
      // Go back a few days to ensure we're not asking for today's incomplete data
      toDate.setDate(toDate.getDate() - 3);
      
      const fromDate = new Date(toDate);
      fromDate.setDate(fromDate.getDate() - period.days);

      const fromStr = fromDate.toISOString().split('T')[0];
      const toStr = toDate.toISOString().split('T')[0];

      console.log(`Fetching chart data for ${stock.ticker} from ${fromStr} to ${toStr}`);

      // Use the correct endpoint format from the API guide
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${stock.ticker}/aggregates?multiplier=1&timespan=day&from=${fromStr}&to=${toStr}&adjusted=true`
      );
      const data = await response.json();
      
      console.log('Chart data response:', data);
      
      if (data.status === 'OK' && data.results && data.results.length > 0) {
        setChartData(data.results);
      } else {
        // If still no data, try using previous day data or create mock data
        console.log('No aggregated data found, trying previous day data...');
        try {
          const prevResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${stock.ticker}/prev`);
          const prevData = await prevResponse.json();
          
          if (prevData.status === 'OK' && prevData.results && prevData.results.length > 0) {
            // Create a simple chart with just the previous day's data
            const dayData = prevData.results[0];
            const mockData = [
              { c: dayData.o, h: dayData.h, l: dayData.l, o: dayData.o, t: dayData.t - 86400000 }, // Previous day
              { c: dayData.c, h: dayData.h, l: dayData.l, o: dayData.o, t: dayData.t } // Current day
            ];
            setChartData(mockData);
          } else {
            setChartData([]);
          }
        } catch (prevErr) {
          console.error('Error fetching previous day data:', prevErr);
          setChartData([]);
        }
      }
    } catch (err) {
      console.error('Error fetching chart data:', err);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  // Get current holdings for this stock
  const getCurrentHoldings = () => {
    if (!portfolio || !portfolio.positions) return 0;
    const position = portfolio.positions.find(pos => pos.assetSymbol === stock.ticker);
    return position ? position.quantity : 0;
  };

  // Validate buy order
  const validateBuyOrder = () => {
    if (!portfolio) return false;
    const totalCost = stock.ask * buyQuantity;
    return portfolio.cash >= totalCost;
  };

  // Validate sell order  
  const validateSellOrder = () => {
    const currentHoldings = getCurrentHoldings();
    return currentHoldings >= sellQuantity;
  };

  // Handle buy transaction
  const handleBuy = async () => {
    if (!validateBuyOrder()) {
      const totalCost = stock.ask * buyQuantity;
      setErrorMessage(`Insufficient funds. You need $${totalCost.toFixed(2)} but only have $${portfolio?.cash?.toFixed(2) || 0}.`);
      return;
    }

    setBuyLoading(true);
    setErrorMessage('');
    
    try {
      const userId = user._id || localStorage.getItem('userId');
      const response = await transactionService.buyAssetWithPolygonPrice(userId, stock.ticker, buyQuantity, stock.ask);
      
      if (response.success) {
        setSuccessMessage(`Successfully purchased ${buyQuantity} share${buyQuantity !== 1 ? 's' : ''} of ${stock.ticker}!`);
        await fetchPortfolioData(); // Refresh portfolio data
        await loadPortfolioData(); // Refresh GameContext data for header
        setBuyQuantity(1); // Reset quantity
      } else {
        setErrorMessage(response.message || 'Failed to complete purchase');
      }
    } catch (error) {
      console.error('Buy error:', error);
      setErrorMessage(error.message || 'Failed to complete purchase. Please try again.');
    } finally {
      setBuyLoading(false);
    }
  };

  // Handle sell transaction
  const handleSell = async () => {
    const currentHoldings = getCurrentHoldings();
    
    if (!validateSellOrder()) {
      setErrorMessage(`Insufficient shares. You have ${currentHoldings} share${currentHoldings !== 1 ? 's' : ''} but trying to sell ${sellQuantity}. Short selling is not allowed.`);
      return;
    }

    setSellLoading(true);
    setErrorMessage('');
    
    try {
      const userId = user._id || localStorage.getItem('userId');
      const response = await transactionService.sellAssetWithPolygonPrice(userId, stock.ticker, sellQuantity, stock.bid);
      
      if (response.success) {
        setSuccessMessage(`Successfully sold ${sellQuantity} share${sellQuantity !== 1 ? 's' : ''} of ${stock.ticker}!`);
        await fetchPortfolioData(); // Refresh portfolio data
        await loadPortfolioData(); // Refresh GameContext data for header
        setSellQuantity(1); // Reset quantity
      } else {
        setErrorMessage(response.message || 'Failed to complete sale');
      }
    } catch (error) {
      console.error('Sell error:', error);
      setErrorMessage(error.message || 'Failed to complete sale. Please try again.');
    } finally {
      setSellLoading(false);
    }
  };

  const formatDescription = (description) => {
    if (!description) return '';
    const maxLength = 200;
    if (description.length <= maxLength || descriptionExpanded) {
      return description;
    }
    return description.substring(0, maxLength) + '...';
  };

  const formatPrice = (value) => `$${value.toFixed(2)}`;
  const formatCurrency = (value) => {
    if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    return `$${value.toLocaleString()}`;
  };

  const getMaxPrice = () => Math.max(...chartData.map(d => d.h));
  const getMinPrice = () => Math.min(...chartData.map(d => d.l));

  const renderChart = () => {
    if (loading) {
      return (
        <div className="chart-loading">
          <div className="chart-spinner"></div>
          <p>Loading chart data...</p>
        </div>
      );
    }

    if (!chartData || chartData.length === 0) {
      return (
        <div className="chart-empty">
          <p>No chart data available for this period</p>
        </div>
      );
    }

    const maxPrice = getMaxPrice();
    const minPrice = getMinPrice();
    const priceRange = maxPrice - minPrice;
    const chartHeight = 200;
    const chartWidth = 600;

    return (
      <div className="stock-chart">
        <svg width="100%" height={chartHeight + 40} viewBox={`0 0 ${chartWidth} ${chartHeight + 40}`}>
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
            const y = chartHeight * ratio + 20;
            const price = maxPrice - (priceRange * ratio);
            return (
              <g key={i}>
                <line
                  x1="60"
                  y1={y}
                  x2={chartWidth - 20}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  strokeDasharray="5,5"
                />
                <text
                  x="50"
                  y={y + 5}
                  fill="#6b7280"
                  fontSize="12"
                  textAnchor="end"
                >
                  ${price.toFixed(0)}
                </text>
              </g>
            );
          })}

          {/* Price line */}
          <path
            d={chartData.map((point, index) => {
              const x = 60 + (index / (chartData.length - 1)) * (chartWidth - 80);
              const y = 20 + ((maxPrice - point.c) / priceRange) * chartHeight;
              return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
            }).join(' ')}
            stroke="#059669"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Area fill */}
          <path
            d={`
              ${chartData.map((point, index) => {
                const x = 60 + (index / (chartData.length - 1)) * (chartWidth - 80);
                const y = 20 + ((maxPrice - point.c) / priceRange) * chartHeight;
                return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
              }).join(' ')}
              L ${60 + (chartWidth - 80)} ${chartHeight + 20}
              L 60 ${chartHeight + 20}
              Z
            `}
            fill="url(#priceGradient)"
            opacity="0.3"
          />

          {/* Gradient definition */}
          <defs>
            <linearGradient id="priceGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#059669" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#059669" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          {/* Data points */}
          {chartData.map((point, index) => {
            const x = 60 + (index / (chartData.length - 1)) * (chartWidth - 80);
            const y = 20 + ((maxPrice - point.c) / priceRange) * chartHeight;
            return (
              <circle
                key={index}
                cx={x}
                cy={y}
                r="3"
                fill="#059669"
                stroke="white"
                strokeWidth="2"
                className="chart-point"
              />
            );
          })}
        </svg>
      </div>
    );
  };

  if (!isOpen || !stock) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="stock-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        
        {/* Header */}
        <div className="modal-header">
          <div className="stock-header-info">
            <div className="stock-logo-large">
              <img 
                src={stock.logoUrl}
                alt={`${stock.name} logo`}
                onError={(e) => {
                  if (e.target.src === stock.logoUrl) {
                    e.target.src = stock.iconUrl;
                  } else {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect width="80" height="80" fill="%23059669" rx="12"/><text x="40" y="50" font-family="Arial, sans-serif" font-size="24" font-weight="bold" fill="white" text-anchor="middle">' + stock.ticker[0] + '</text></svg>';
                  }
                }}
              />
            </div>
            <div className="stock-title-info">
              <h2 className="stock-modal-title">{stock.name}</h2>
              <div className="stock-modal-ticker">{stock.ticker}</div>
              <div className="stock-modal-price">{formatPrice(stock.close)}</div>
            </div>
          </div>
          
          <div className="stock-metrics-summary">
            <div className="metric-summary">
              <span className="metric-label">Market Cap</span>
              <span className="metric-value">{formatCurrency(stock.marketCap)}</span>
            </div>
            <div className="metric-summary">
              <span className="metric-label">Buy Price</span>
              <span className="metric-value buy-price">{formatPrice(stock.ask)}</span>
            </div>
            <div className="metric-summary">
              <span className="metric-label">Sell Price</span>
              <span className="metric-value sell-price">{formatPrice(stock.bid)}</span>
            </div>
            <div className="metric-summary">
              <span className="metric-label">Volume</span>
              <span className="metric-value">{stock.volume.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Company Description */}
        {companyDetails?.description && (
          <div className="company-description">
            <h3>About {stock.name}</h3>
            <p className="description-text">
              {formatDescription(companyDetails.description)}
            </p>
            {companyDetails.description.length > 200 && (
              <button 
                className="expand-button"
                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
              >
                {descriptionExpanded ? 'Show Less' : 'Show More'}
              </button>
            )}
          </div>
        )}

        {/* Chart Section */}
        <div className="chart-section">
          <div className="chart-header">
            <h3>Price Chart</h3>
            <div className="period-selector">
              {periods.map(period => (
                <button
                  key={period.value}
                  className={`period-button ${selectedPeriod === period.value ? 'active' : ''}`}
                  onClick={() => setSelectedPeriod(period.value)}
                >
                  {period.label}
                </button>
              ))}
            </div>
          </div>
          
          {renderChart()}
        </div>

        {/* Trading Actions */}
        <div className="trading-actions">
          <h3>Trading Actions</h3>
          
          {/* Error/Success Messages */}
          {errorMessage && (
            <div style={{
              background: 'linear-gradient(135deg, #fef2f2 0%, #fef7f7 100%)',
              border: '2px solid #fee2e2',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              color: '#dc2626',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              {errorMessage}
            </div>
          )}
          
          {successMessage && (
            <div style={{
              background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
              border: '2px solid #d1fae5',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              color: '#059669',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              ✅ {successMessage}
            </div>
          )}
          
          {/* Portfolio Info */}
          {portfolio && (
            <div style={{
              background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '16px'
            }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Available Cash</div>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#059669' }}>
                  ${portfolio.cash?.toFixed(2) || '0.00'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                  {stock.ticker} Shares
                </div>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#374151' }}>
                  {getCurrentHoldings()} shares
                </div>
              </div>
            </div>
          )}
          
          <div className="trading-grid">
            {/* Buy Section */}
            <div className="trading-section buy-section">
              <div className="trading-header">
                <h4>Buy {stock.ticker}</h4>
                <div className="price-display buy-price-display">
                  {formatPrice(stock.ask)} per share
                </div>
              </div>
              
              <div className="quantity-controls">
                <label className="quantity-label">Quantity (shares)</label>
                <div className="quantity-input-group">
                  <button 
                    className="quantity-btn minus"
                    onClick={() => setBuyQuantity(Math.max(1, buyQuantity - 1))}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    className="quantity-input"
                    value={buyQuantity}
                    onChange={(e) => setBuyQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                  />
                  <button 
                    className="quantity-btn plus"
                    onClick={() => setBuyQuantity(buyQuantity + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
              
              <div className="total-amount">
                <span className="total-label">Total Cost:</span>
                <span className="total-value">{formatPrice(stock.ask * buyQuantity)}</span>
              </div>
              
              <button 
                className="action-button buy-button"
                onClick={handleBuy}
                disabled={buyLoading || !validateBuyOrder() || portfolioLoading}
                style={{
                  opacity: (buyLoading || !validateBuyOrder() || portfolioLoading) ? 0.6 : 1,
                  cursor: (buyLoading || !validateBuyOrder() || portfolioLoading) ? 'not-allowed' : 'pointer'
                }}
              >
                {buyLoading ? 'PROCESSING...' : `BUY ${buyQuantity} SHARE${buyQuantity !== 1 ? 'S' : ''}`}
              </button>
            </div>

            {/* Sell Section */}
            <div className="trading-section sell-section">
              <div className="trading-header">
                <h4>Sell {stock.ticker}</h4>
                <div className="price-display sell-price-display">
                  {formatPrice(stock.bid)} per share
                </div>
              </div>
              
              <div className="quantity-controls">
                <label className="quantity-label">Quantity (shares)</label>
                <div className="quantity-input-group">
                  <button 
                    className="quantity-btn minus"
                    onClick={() => setSellQuantity(Math.max(1, sellQuantity - 1))}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    className="quantity-input"
                    value={sellQuantity}
                    onChange={(e) => setSellQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                  />
                  <button 
                    className="quantity-btn plus"
                    onClick={() => setSellQuantity(sellQuantity + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
              
              <div className="total-amount">
                <span className="total-label">Total Value:</span>
                <span className="total-value">{formatPrice(stock.bid * sellQuantity)}</span>
              </div>
              
              <button 
                className="action-button sell-button"
                onClick={handleSell}
                disabled={sellLoading || !validateSellOrder() || portfolioLoading}
                style={{
                  opacity: (sellLoading || !validateSellOrder() || portfolioLoading) ? 0.6 : 1,
                  cursor: (sellLoading || !validateSellOrder() || portfolioLoading) ? 'not-allowed' : 'pointer'
                }}
              >
                {sellLoading ? 'PROCESSING...' : `SELL ${sellQuantity} SHARE${sellQuantity !== 1 ? 'S' : ''}`}
              </button>
            </div>
          </div>
        </div>

        {/* Additional Company Info */}
        {companyDetails && (
          <div className="company-additional-info">
            <div className="info-grid">
              {companyDetails.total_employees && (
                <div className="info-item">
                  <span className="info-label">Employees</span>
                  <span className="info-value">{companyDetails.total_employees.toLocaleString()}</span>
                </div>
              )}
              {companyDetails.list_date && (
                <div className="info-item">
                  <span className="info-label">Listed</span>
                  <span className="info-value">{new Date(companyDetails.list_date).getFullYear()}</span>
                </div>
              )}
              {companyDetails.primary_exchange && (
                <div className="info-item">
                  <span className="info-label">Exchange</span>
                  <span className="info-value">{companyDetails.primary_exchange}</span>
                </div>
              )}
              {companyDetails.homepage_url && (
                <div className="info-item">
                  <span className="info-label">Website</span>
                  <a 
                    href={companyDetails.homepage_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="info-link"
                  >
                    Visit Site
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockDetailsModal;